/**
 * subbass-synth.js — суб-гармонический синтез, октава ВНИЗ (window.SubBassSynth)
 *
 * Класс Waves LoAir / «Sub-bass»-плагинов: из полосы 55–120 Гц источника
 * генерируется реальный саб 27–60 Гц, которого в записи нет. Заведён после
 * разбора с Левоном (19.08.2026): он прошёл весь воркфлоу на наших
 * инструментах (стемы → правка дорожек → сведение в /audio-mixer) и уходил
 * во внешнюю программу ровно за одним — пер-дорожечным сабом. Замер его
 * рендера показал, что его цепочка саб ПОТЕРЯЛА (−5..8 дБ на 25–63 Гц) —
 * ручка у нас закрывает и разрыв, и его реальную проблему.
 *
 * Метод (числа — из прототипа tests/scratch/subbass-proto-scratch.mjs):
 *   BP 55–120 (биквады ×2) → огибающая (атака 5 мс / релиз 50 мс) →
 *   делитель частоты на нулевых пересечениях (меандр f/2, гейт −60 дБFS —
 *   иначе делитель молотит шумом в паузах) × огибающая → LP 60 Гц ×3 →
 *   HP 25 Гц → нормировка RMS к полосе-источнику (кноб 100% = «столько же,
 *   сколько баса было в 55–120»).
 *   Точность на тоне 80 Гц: выход = 40 Гц, остаток 80 Гц −71.6 дБ,
 *   160 Гц −92.7; на реальном миксе слой аддитивен: +8.5 дБ @25 /
 *   +2.7 @40 при Δ = 0.0 от 79 Гц и выше. Скорость ~230× реалтайма.
 *
 * КОНТРАКТ ДВУХ ПУТЕЙ: слой считается ИЗ БУФЕРА один раз (кэш у вызывателя)
 * и проигрывается вторым BufferSource в живом графе И в OfflineAudioContext
 * с теми же параметрами старта — прослушка и экспорт совпадают по
 * построению. Ручка = гейн слоя, пересинтез не нужен.
 *
 * synthAsync ломтит работу (~5 c аудио на макрозадачу): 5-минутный трек
 * ≈ 1.3 c CPU — одним куском это заметный фриз UI.
 */
(function () {
    'use strict';
    if (window.SubBassSynth) return;

    function coef(kind, fc, sr) {
        var Q = 0.7071;
        var w = 2 * Math.PI * fc / sr, cw = Math.cos(w), alpha = Math.sin(w) / (2 * Q);
        var b0, b1, b2;
        if (kind === 'lp') { b0 = (1 - cw) / 2; b1 = 1 - cw; b2 = (1 - cw) / 2; }
        else { b0 = (1 + cw) / 2; b1 = -(1 + cw); b2 = (1 + cw) / 2; }
        var a0 = 1 + alpha;
        return { b0: b0 / a0, b1: b1 / a0, b2: b2 / a0, a1: -2 * cw / a0, a2: (1 - alpha) / a0 };
    }
    function makeRunner(c) {
        var x1 = 0, x2 = 0, y1 = 0, y2 = 0;
        return function (buf, from, to) {
            for (var i = from; i < to; i++) {
                var v = c.b0 * buf[i] + c.b1 * x1 + c.b2 * x2 - c.a1 * y1 - c.a2 * y2;
                x2 = x1; x1 = buf[i]; y2 = y1; y1 = v; buf[i] = v;
            }
        };
    }

    /* Синтез суб-слоя из АУДИОБУФЕРА (моно-микс каналов).
       Возвращает Float32Array той же длины (моно). */
    async function synthAsync(audioBuffer, onProgress) {
        var sr = audioBuffer.sampleRate, n = audioBuffer.length;
        var mono = new Float32Array(n);
        var ch = audioBuffer.numberOfChannels, c, i;
        for (c = 0; c < ch; c++) {
            var d = audioBuffer.getChannelData(c);
            for (i = 0; i < n; i++) mono[i] += d[i] / ch;
        }
        var band = mono; // фильтруем на месте (mono больше не нужен как оригинал)
        var runners = [
            makeRunner(coef('hp', 55, sr)), makeRunner(coef('hp', 55, sr)),
            makeRunner(coef('lp', 120, sr)), makeRunner(coef('lp', 120, sr)),
        ];
        var CH = sr * 5; // ломоть ~5 c
        function yieldTask() {
            if (typeof scheduler !== 'undefined' && scheduler.yield) return scheduler.yield();
            return new Promise(function (r) { setTimeout(r, 0); });
        }
        var pos;
        for (pos = 0; pos < n; pos += CH) {
            var end = Math.min(n, pos + CH);
            for (var rIdx = 0; rIdx < runners.length; rIdx++) runners[rIdx](band, pos, end);
            if (onProgress) onProgress(0.4 * end / n);
            await yieldTask();
        }
        // огибающая + делитель + гейт — один проход
        var aAtk = Math.exp(-1 / (0.005 * sr)), aRel = Math.exp(-1 / (0.05 * sr));
        var GATE = 0.001; // −60 дБFS
        var sub = new Float32Array(n);
        var e = 0, sign = 1, prev = band[0], rmsBand = 0;
        for (pos = 0; pos < n; pos += CH) {
            var end2 = Math.min(n, pos + CH);
            for (i = pos; i < end2; i++) {
                var a = band[i] < 0 ? -band[i] : band[i];
                e = a > e ? aAtk * e + (1 - aAtk) * a : aRel * e + (1 - aRel) * a;
                if (prev <= 0 && band[i] > 0 && e > GATE) sign = -sign;
                sub[i] = e > GATE ? sign * e : 0;
                prev = band[i];
                rmsBand += band[i] * band[i];
            }
            if (onProgress) onProgress(0.4 + 0.3 * end2 / n);
            await yieldTask();
        }
        var post = [
            makeRunner(coef('lp', 60, sr)), makeRunner(coef('lp', 60, sr)), makeRunner(coef('lp', 60, sr)),
            makeRunner(coef('hp', 25, sr)),
        ];
        var rmsSub = 0;
        for (pos = 0; pos < n; pos += CH) {
            var end3 = Math.min(n, pos + CH);
            for (var pIdx = 0; pIdx < post.length; pIdx++) post[pIdx](sub, pos, end3);
            for (i = pos; i < end3; i++) rmsSub += sub[i] * sub[i];
            if (onProgress) onProgress(0.7 + 0.25 * end3 / n);
            await yieldTask();
        }
        var g = rmsSub > 1e-12 ? Math.sqrt(rmsBand / rmsSub) : 0;
        // страховка от вырожденных случаев (щелчок-вход и т.п.)
        if (g > 8) g = 8;
        for (i = 0; i < n; i++) sub[i] *= g;

        /* Presence-ветка (20.08, Левон: «в наушниках не слышу»): чистый суб
           <60 Гц большинство наушников/ноутбуков физически не воспроизводит.
           Класс LOX/MaxxBass решает это ГАРМОНИКАМИ суба в слышимой полосе:
           асимметричный tanh (bias даёт ЧЁТНЫЕ — октава суба 2f «звучит как
           тот же бас») → полоса 60–180 → подмес. Параметры замером
           (bias .3 / drive 3 / mix .3): октава на −16 дБ от суба, без
           «бубнежа» (bias .5+ раздувает фундаментал). DC от асимметрии
           снимает полосовой HP60. */
        // PMIX 0.3 → 0.45 (20.08, раунд 3, Левон: «чуть слышно — усилить
        // нельзя?»): по свипу mix 0.45 при bias 0.3/drive 3 держит октаву
        // на −14 дБ от суба, всё ещё без «бубнежа».
        var BIAS = 0.3, DRIVE = 3, PMIX = 0.45;
        var normT = Math.tanh(DRIVE * (1 + BIAS)) - Math.tanh(DRIVE * BIAS);
        var harm = new Float32Array(n);
        // пик суба для масштаба сатурации (нормируем вход кривой к ~±1)
        var subPeak = 0;
        for (i = 0; i < n; i++) { var av = sub[i] < 0 ? -sub[i] : sub[i]; if (av > subPeak) subPeak = av; }
        var inScale = subPeak > 1e-9 ? 1 / subPeak : 0;
        for (i = 0; i < n; i++) {
            var sx = sub[i] * inScale;
            harm[i] = (Math.tanh(DRIVE * (sx + BIAS)) - Math.tanh(DRIVE * BIAS)) / normT * subPeak;
        }
        var presence = [
            makeRunner(coef('hp', 60, sr)), makeRunner(coef('hp', 60, sr)),
            makeRunner(coef('lp', 180, sr)), makeRunner(coef('lp', 180, sr)),
        ];
        for (pos = 0; pos < n; pos += CH) {
            var end4 = Math.min(n, pos + CH);
            for (var hIdx = 0; hIdx < presence.length; hIdx++) presence[hIdx](harm, pos, end4);
            await yieldTask();
        }
        for (i = 0; i < n; i++) sub[i] += PMIX * harm[i];

        if (onProgress) onProgress(1);
        return sub;
    }

    /* Обернуть слой в AudioBuffer данного контекста (моно). */
    function toBuffer(ctx, layer, sr) {
        var buf = ctx.createBuffer(1, layer.length, sr);
        buf.copyToChannel ? buf.copyToChannel(layer, 0) : buf.getChannelData(0).set(layer);
        return buf;
    }

    window.SubBassSynth = { synthAsync: synthAsync, toBuffer: toBuffer };
})();
